import type { ReactNode } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { can } from "@ichegou/contracts";
import { api, authPost, call } from "../lib/api.ts";
import { supportApi } from "../lib/support.ts";
import { useCity, useSession } from "../lib/session.tsx";
import { useTheme, type ThemePref } from "../lib/theme.tsx";
import { Icon, type IconName } from "./Icon.tsx";
import { SafetyBanner } from "./SafetyBanner.tsx";
import { Initials, Skeleton } from "./ui.tsx";
import { useTripTypeLabels } from "../lib/trip-type-labels.ts";

type NavItem = { to: string; label: string; icon: IconName; live?: boolean; badge?: number; show: boolean };

export function Shell({ children }: { children: ReactNode }) {
  const session = useSession();
  // Trip type names label trips everywhere: pages render once they are here (or failed to load).
  const labels = useTripTypeLabels();
  const { cityId, setCityId, cities } = useCity();
  const p = session.role.permissions;
  const canDrivers = can(p, "motoristas", "ver");
  const pending = useQuery({
    queryKey: ["drivers", cityId, "pending-count"],
    queryFn: () => call(api.admin.drivers["pending-count"].get({ query: { cityId } })),
    enabled: canDrivers && !!cityId,
    refetchInterval: 60_000,
    select: (d) => d.pending + d.liveness + d.documents,
  });
  const canPayouts = can(p, "repasses", "ver");
  const payoutAttention = useQuery({
    queryKey: ["payouts", "attention"],
    queryFn: () => call(api.admin.payouts.attention.get()),
    enabled: canPayouts,
    refetchInterval: 60_000,
    select: (d) => d.failed + d.held,
  });

  const canSupport = can(p, "suporte", "ver");
  const supportWaiting = useQuery({
    queryKey: ["support", "badge"],
    queryFn: () => supportApi.list({ status: "waiting_staff" }),
    enabled: canSupport,
    refetchInterval: 30_000,
    retry: false,
    select: (d) => d.counts.waiting_staff + d.counts.open,
  });

  const canSafety = can(p, "seguranca", "ver");
  const safetyOpen = useQuery({
    queryKey: ["safety", cityId, "summary"],
    queryFn: () => call(api.admin.safety.summary.get({ query: { cityId } })),
    enabled: canSafety && !!cityId,
    refetchInterval: 30_000,
    select: (d) => d.openAlerts + (d.openIncidents ?? 0),
  });

  const sections: { head: string; items: NavItem[] }[] = [
    {
      head: "Operação",
      items: [
        { to: "/", label: "Visão geral", icon: "grid", show: can(p, "corridas", "ver") },
        { to: "/ao-vivo/corridas", label: "Corridas ao vivo", icon: "radar", live: true, show: can(p, "corridas", "ver") },
        { to: "/ao-vivo/motoristas", label: "Motoristas ao vivo", icon: "pin", live: true, show: canDrivers },
        { to: "/corridas", label: "Corridas", icon: "car", show: can(p, "corridas", "ver") },
        { to: "/despacho", label: "Despacho H3", icon: "zap", show: can(p, "corridas", "ver") },
        { to: "/seguranca", label: "Segurança", icon: "shield", badge: safetyOpen.data, show: canSafety },
      ],
    },
    {
      head: "Pessoas",
      items: [
        { to: "/motoristas", label: "Motoristas", icon: "idcard", show: canDrivers },
        { to: "/cadastros", label: "Cadastros de motoristas", icon: "user", badge: pending.data, show: canDrivers },
        { to: "/passageiros", label: "Passageiros", icon: "users", show: can(p, "passageiros", "ver") },
        { to: "/equipe", label: "Equipe e permissões", icon: "key", show: can(p, "equipe", "ver") },
      ],
    },
    {
      head: "Dinheiro",
      items: [
        { to: "/financeiro", label: "Financeiro", icon: "wallet", show: can(p, "financeiro", "ver") },
        { to: "/repasses", label: "Repasses Pix", icon: "send", badge: payoutAttention.data, show: canPayouts },
        { to: "/cupons", label: "Cupons", icon: "tag", show: can(p, "tarifas", "ver") },
      ],
    },
    {
      head: "Sistema",
      items: [
        { to: "/suporte", label: "Suporte", icon: "chat", badge: supportWaiting.data, show: canSupport },
        { to: "/notificacoes", label: "Notificações", icon: "bell", show: canDrivers || can(p, "passageiros", "ver") },
        { to: "/cidades", label: "Cidades", icon: "map", show: can(p, "tarifas", "ver") },
        { to: "/configuracoes", label: "Configurações", icon: "gear", show: can(p, "tarifas", "ver") },
      ],
    },
  ];

  return (
    <div className="frame">
      <aside className="sidebar">
        <span className="brand">
          <img className="brand-mark" src="/icon-96.png" alt="" />
          <BrandLogo />
          <span className="muted" style={{ fontSize: 12 }}>admin</span>
        </span>
        <nav className="nav" aria-label="Admin">
          {sections.map((s) => {
            const items = s.items.filter((i) => i.show);
            if (!items.length) return null;
            return (
              <div key={s.head} className="nav">
                <span className="navhead">{s.head}</span>
                {items.map((i) => (
                  <Link key={i.to} to={i.to} className="navlink" activeOptions={{ exact: i.to === "/" }}>
                    <Icon name={i.icon} />
                    <span className="grow">{i.label}</span>
                    {i.live && <span className="live-dot" aria-hidden="true" />}
                    {!!i.badge && <span className="badge-count" aria-label={`${i.badge} aguardando`}>{i.badge}</span>}
                  </Link>
                ))}
              </div>
            );
          })}
        </nav>
        <div className="sidebar-foot">
          <label className="sr-only" htmlFor="city">Cidade</label>
          <select id="city" className="select" value={cityId} onChange={(e) => setCityId(e.target.value)} disabled={cities.length < 2}>
            {cities.map((c) => (
              <option key={c.id} value={c.id}>{c.name} — {c.state}</option>
            ))}
          </select>
          <ThemeSwitch />
          <Me />
        </div>
      </aside>
      <main className="main">
        <SafetyBanner enabled={canSafety} />
        {labels.isPending ? <Skeleton h={320} /> : children}
      </main>
    </div>
  );
}

const THEMES: [ThemePref, string, "sun" | "moon" | "monitor"][] = [["light", "Claro", "sun"], ["dark", "Escuro", "moon"], ["system", "Sistema", "monitor"]];

function ThemeSwitch() {
  const { pref, setPref } = useTheme();
  return (
    <div className="segmented theme-switch" role="group" aria-label="Aparência">
      {THEMES.map(([t, label, icon]) => (
        <button key={t} type="button" aria-pressed={pref === t} onClick={() => setPref(t)} title={`Aparência: ${label.toLowerCase()}`}>
          <Icon name={icon} size={14} />{label}
        </button>
      ))}
    </div>
  );
}

function Me() {
  const session = useSession();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const initials = session.user.name.split(/\s+/).filter(Boolean).map((w) => w[0]).filter((_, i, a) => i === 0 || i === a.length - 1).join("").toUpperCase();
  async function signOut() {
    await call(api.admin.session.lock.post()).catch(() => {});
    await authPost("/sign-out").catch(() => {});
    qc.clear();
    await navigate({ to: "/entrar", search: { erro: undefined } });
  }
  return (
    <div className="me">
      <Initials text={initials} photo={session.user.image} />
      <span className="me-text">
        <b>{session.user.name}</b>
        <span>{session.role.name}</span>
      </span>
      <button type="button" className="btn btn-icon" onClick={signOut} aria-label="Sair" title="Sair">
        <Icon name="logout" size={16} />
      </button>
    </div>
  );
}

/** The wordmark; CSS shows the white-letter version under the dark theme. */
export function BrandLogo() {
  return (
    <>
      <img className="brand-logo on-light" src="/logo.png" alt="ichegou" />
      <img className="brand-logo on-dark" src="/logo-dark.png" alt="ichegou" />
    </>
  );
}

export function PageHead({ icon, title, sub, children }: { icon?: IconName; title: string; sub?: ReactNode; children?: ReactNode }) {
  return (
    <header className="page-head">
      <span className="page-title">
        {icon && <Icon name={icon} size={20} />}
        <h1>{title}</h1>
        {sub && <span className="sub">{sub}</span>}
      </span>
      {children}
    </header>
  );
}
